
/**
 * Core service for sharing game plans
 */
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { GamePlan } from '@/types/gamePlanner';
import type { SharedGamePlan } from '@/types/gamePlan';
import { Node, Edge } from '@xyflow/react';
import { generateShareId } from './shareIdGenerator';
import { incrementViewCount, incrementImportCount, recordReferralConversion } from './contentStats';
import { generateShareableUrl } from './referralService';

/**
 * Creates a shared version of a game plan that can be accessed by others
 * @param gamePlan The game plan to share
 * @param referralCode Optional referral code to track the share source
 * @returns The unique share ID for the created shared game plan
 */
export async function createSharedGamePlan(gamePlan: GamePlan, referralCode?: string): Promise<string | null> {
  try {
    if (!gamePlan || !gamePlan.id) {
      throw new Error("Invalid game plan data");
    }

    console.log("Creating shared game plan for:", gamePlan.id);
    
    // Generate a unique share ID
    const shareId = generateShareId();
    
    // Get the current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      throw new Error("Authentication required to share game plans");
    }
    
    // Prepare the data for the shared plan
    const sharedPlanData = {
      original_id: gamePlan.id,
      creator_id: user.id,
      share_id: shareId,
      name: gamePlan.name || "Untitled Game Plan",
      description: gamePlan.description || "",
      flow_data: gamePlan.flow_data || {},
      thumbnail: gamePlan.thumbnail || null,
      referral_code: referralCode || null,
      view_count: 0,
      import_count: 0
    };
    
    // Insert directly into the database
    const { data, error } = await supabase
      .from('shared_game_plans')
      .insert(sharedPlanData)
      .select('share_id')
      .single();
    
    if (error) {
      console.error("Error creating shared game plan:", error);
      throw new Error(`Failed to create shared game plan: ${error.message}`);
    }
    
    if (!data || !data.share_id) {
      console.error("No share ID returned:", data);
      throw new Error("No share ID returned from database");
    }
    
    console.log("Shared game plan created successfully with ID:", data.share_id);
    return data.share_id;
  } catch (error: unknown) {
    console.error("Error in createSharedGamePlan:", error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Gets a shared game plan using its share ID
 * @param shareId The unique share ID of the game plan
 * @returns The shared game plan data
 */
export async function getSharedGamePlan(shareId: string): Promise<SharedGamePlan> {
  try {
    if (!shareId) {
      throw new Error("Share ID is required");
    }
    
    console.log("Fetching shared game plan with ID:", shareId);
    
    // Query the database directly
    const { data, error } = await supabase
      .from('shared_game_plans')
      .select('*')
      .eq('share_id', shareId)
      .single();
    
    if (error) {
      console.error("Error fetching shared game plan:", error);
      if (error.code === 'PGRST116') {
        throw new Error("Shared game plan not found");
      }
      throw new Error(`Failed to fetch shared game plan: ${error.message}`);
    }
    
    if (!data) {
      console.error("Shared game plan not found with ID:", shareId);
      throw new Error("Shared game plan not found");
    }
    
    // Increment the view count using the unified service
    try {
      await incrementViewCount('game_plan', shareId);
    } catch (viewError) {
      // Non-critical error, just log it
      console.warn("Error incrementing view count:", viewError);
    }
    
    return {
      id: data.id,
      name: data.name,
      description: data.description || undefined,
      flow_data: data.flow_data || { nodes: [], edges: [] },
      user_id: data.creator_id || '',
      is_public: true,
      created_at: data.created_at || '',
      updated_at: data.updated_at || '',
      share_id: data.share_id,
      creator_id: data.creator_id || '',
      view_count: data.view_count || 0,
      import_count: data.import_count || 0,
    };
  } catch (error: unknown) {
    console.error("Error in getSharedGamePlan:", error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Imports a shared game plan into the user's library
 * @param shareId The unique share ID of the game plan to import
 * @returns The created game plan or null if import failed
 */
export async function importSharedGamePlan(shareId: string): Promise<GamePlan | null> {
  try {
    if (!shareId) {
      throw new Error("Share ID is required");
    }
    
    console.log("Importing shared game plan:", shareId);
    
    // Get the current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      throw new Error("Authentication required to import game plans");
    }
    
    // Get the shared plan
    const sharedPlan = await getSharedGamePlan(shareId);
    
    if (!sharedPlan) {
      throw new Error("Shared plan not found");
    }
    
    // Process flow_data to ensure it has the correct structure
    let processedFlowData: { nodes: Node[], edges: Edge[] } = { nodes: [], edges: [] };
    if (sharedPlan.flow_data) {
      if (typeof sharedPlan.flow_data === 'string') {
        try {
          const parsed = JSON.parse(sharedPlan.flow_data);
          processedFlowData = {
            nodes: Array.isArray(parsed?.nodes) ? parsed.nodes : [],
            edges: Array.isArray(parsed?.edges) ? parsed.edges : []
          };
        } catch (e) {
          console.error("Error parsing flow_data:", e);
        }
      } else if (typeof sharedPlan.flow_data === 'object') {
        processedFlowData = {
          nodes: Array.isArray(sharedPlan.flow_data?.nodes) ? sharedPlan.flow_data.nodes : [],
          edges: Array.isArray(sharedPlan.flow_data?.edges) ? sharedPlan.flow_data.edges : []
        };
      }
    }
    
    // Create a new game plan from the shared data
    const gamePlanData = {
      user_id: user.id,
      name: `${sharedPlan.name} (Imported)`,
      description: sharedPlan.description,
      flow_data: processedFlowData,
      thumbnail: sharedPlan.thumbnail,
      is_public: false,
      is_template: false
    };
    
    const { data, error } = await supabase
      .from('game_plans')
      .insert(gamePlanData)
      .select()
      .single();
    
    if (error) {
      console.error("Error creating game plan from shared data:", error);
      throw new Error(`Failed to import game plan: ${error.message}`);
    }
    
    // Increment the import count using the unified service
    try {
      await incrementImportCount('game_plan', shareId);
    } catch (importError) {
      // Non-critical error, just log it
      console.warn("Error incrementing import count:", importError);
    }
    
    // If there's a referral code, record the conversion
    if (sharedPlan.referral_code) {
      try {
        await recordReferralConversion(
          sharedPlan.referral_code, 
          'game_plan', 
          shareId, 
          'game_plan_import'
        );
      } catch (referralError) {
        // Non-critical error, just log it
        console.warn("Error recording referral conversion:", referralError);
      }
    }
    
    toast({
      title: "Game plan imported",
      description: "The shared game plan has been added to your library"
    });
    
    // Return the data with the required last_updated property and correct flow_data structure
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      thumbnail: data.thumbnail,
      flow_data: processedFlowData,
      created_at: data.created_at,
      updated_at: data.updated_at,
      user_id: data.user_id,
      is_public: data.is_public,
      is_template: data.is_template,
      is_admin_template: data.is_admin_template,
      last_updated: new Date(data.updated_at).toLocaleDateString()
    };
  } catch (error: unknown) {
    console.error("Error in importSharedGamePlan:", error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

// Directly export functions from the unified content stats service
export { incrementViewCount, incrementImportCount } from './contentStats';

// Re-export functions from other modules for backward compatibility
export { generateShareId } from './shareIdGenerator';
export { generateShareableUrl };
